import React, { useState } from 'react';
import { UserProfile, Ethnicity } from '../../../app/domain/types/index.ts';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Activity, TrendingDown, Info, ArrowUpRight, Droplets, Utensils, Search, BookOpen, Sun, Target, Calendar, CheckCircle, MessageSquare, Users } from 'lucide-react';

interface DashboardProps {
  user: UserProfile;
}

// Mock Data for Charts
const data = [
  { name: 'Week 1', weight: 88, goal: 85 },
  { name: 'Week 2', weight: 87.5, goal: 85 },
  { name: 'Week 3', weight: 86.8, goal: 85 },
  { name: 'Week 4', weight: 86.2, goal: 85 },
  { name: 'Week 5', weight: 85.9, goal: 85 },
  { name: 'Current', weight: 85.5, goal: 85 },
];

const Dashboard: React.FC<DashboardProps> = ({ user }) => {

  // Ethnicity-Aware BMI Calculation logic
  const calculateBMI = (weight: number, height: number) => {
    return (weight / ((height / 100) * (height / 100))).toFixed(1);
  };

  const bmi = calculateBMI(user.weightKg, user.heightCm);
  const bmiNum = parseFloat(bmi);

  // Determine status based on Ethnicity
  const getBmiStatus = (bmi: number, ethnicity: Ethnicity) => {
    // Asian/South Asian cutoffs are lower due to higher risk of metabolic issues at lower BMIs
    const isAsian = ethnicity === Ethnicity.Asian || ethnicity === Ethnicity.SouthAsian || ethnicity === Ethnicity.PacificIslander;

    if (isAsian) {
        if (bmi < 18.5) return { label: 'Underweight', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/30', border: 'border-blue-200 dark:border-blue-800' };
        if (bmi < 23) return { label: 'Healthy Range', color: 'text-health-700 dark:text-health-400', bg: 'bg-health-50 dark:bg-health-900/30', border: 'border-health-200 dark:border-health-800' };
        if (bmi < 27.5) return { label: 'Overweight', color: 'text-energy-600 dark:text-energy-400', bg: 'bg-energy-50 dark:bg-energy-900/30', border: 'border-energy-200 dark:border-energy-800' };
        return { label: 'Obese', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/30', border: 'border-red-200 dark:border-red-800' };
    }

    // Standard (Caucasian, African, Hispanic, etc)
    if (bmi < 18.5) return { label: 'Underweight', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/30', border: 'border-blue-200 dark:border-blue-800' };
    if (bmi < 25) return { label: 'Healthy Range', color: 'text-health-700 dark:text-health-400', bg: 'bg-health-50 dark:bg-health-900/30', border: 'border-health-200 dark:border-health-800' };
    if (bmi < 30) return { label: 'Overweight', color: 'text-energy-600 dark:text-energy-400', bg: 'bg-energy-50 dark:bg-energy-900/30', border: 'border-energy-200 dark:border-energy-800' };
    return { label: 'Obese', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/30', border: 'border-red-200 dark:border-red-800' };
  };

  const status = getBmiStatus(bmiNum, user.ethnicity);

  return (
    <div className="p-6 md:p-10 h-full overflow-y-auto no-scrollbar pb-24 w-full">
      {/* Header */}
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl font-light tracking-tight text-gray-800 dark:text-white">
            Good Morning, <span className="font-semibold text-gray-900 dark:text-white">{user.name.split(' ')[0]}</span>
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-light flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-health-500 shadow-[0_0_8px_rgba(76,175,80,0.6)]"></span>
            Your daily wellness overview
          </p>
        </div>
        <div className="hidden md:flex items-center gap-3">
             <div className="px-4 py-2 bg-white dark:bg-stone-800 rounded-full shadow-sm border border-gray-100 dark:border-stone-700 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <Sun size={16} className="text-energy-500" />
                <span>Today's Focus: <span className="font-medium text-gray-900 dark:text-white">Hydration</span></span>
             </div>
        </div>
      </div>

      {/* 3-Column Grid Layout for Desktop */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">

        {/* === LEFT COLUMN: Metrics & Action Plan (3 cols) === */}
        <div className="md:col-span-3 space-y-6">
             {/* Weight Card */}
            <div className="glass-panel rounded-3xl p-6 relative shadow-md bg-white/80 dark:bg-stone-900/60">
                <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">Current Weight</p>
                <div className="flex items-baseline gap-2">
                    <h2 className="text-6xl font-extrabold text-gray-900 dark:text-white tracking-tighter">{user.weightKg}</h2>
                    <span className="text-lg text-gray-400 mb-1 font-medium">kg</span>
                </div>
                <div className="mt-4 flex items-center text-xs font-medium text-health-700 dark:text-health-400 bg-health-50 dark:bg-health-900/30 w-fit px-2 py-1 rounded-md border border-health-100 dark:border-health-800">
                    <TrendingDown size={14} className="mr-1" />
                    <span>-2.4%</span>
                </div>

                <div className="h-[80px] w-full mt-4 -ml-2">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data}>
                            <defs>
                                <linearGradient id="colorWeightSmall" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#2E7D32" stopOpacity={0.1}/>
                                    <stop offset="95%" stopColor="#2E7D32" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <Area type="monotone" dataKey="weight" stroke="#2E7D32" strokeWidth={3} fillOpacity={1} fill="url(#colorWeightSmall)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

             {/* BMI Card */}
            <div className="glass-panel rounded-3xl p-6 relative shadow-md bg-white/80 dark:bg-stone-900/60">
                 <div className="flex justify-between items-start">
                     <div>
                        <div className="flex items-center gap-2 mb-2">
                            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">Ethnicity BMI</p>
                            <div className="group relative">
                                <Info size={14} className="text-gray-400 cursor-help hover:text-gray-600 dark:hover:text-gray-200" />
                            </div>
                        </div>
                        <div className="flex items-baseline gap-2">
                            <h2 className={`text-6xl font-extrabold tracking-tighter ${status.color}`}>{bmi}</h2>
                        </div>
                        <span className={`mt-4 inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${status.bg} ${status.color} ${status.border} border`}>
                            {status.label}
                        </span>
                     </div>
                 </div>
                 <p className="text-[10px] text-gray-400 mt-4 leading-relaxed">
                    Adjusted for {user.ethnicity}. Standard charts may misclassify risk.
                 </p>
            </div>

            {/* Today's Action Plan (Moved to Left) */}
            <div className="glass-panel rounded-3xl p-6 relative overflow-visible group border-0 shadow-lg bg-white/80 dark:bg-stone-900/60">
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-radial from-health-100/40 to-transparent blur-3xl -mr-16 -mt-16 pointer-events-none dark:opacity-20"></div>

                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-white flex items-center gap-2">
                        <Target size={20} className="text-health-600 dark:text-health-400" />
                        Action Plan
                    </h3>
                    <span className="text-xs font-semibold text-health-600 dark:text-health-400 bg-health-50 dark:bg-health-900/30 px-3 py-1 rounded-full border border-health-100 dark:border-health-800">3 Left</span>
                </div>

                <div className="grid grid-cols-1 gap-3">
                    {[
                        { icon: Droplets, color: 'blue', label: 'Hydration', title: 'Drink 500ml water' },
                        { icon: Utensils, color: 'green', label: 'Lunch Prep', title: 'Grilled Chicken Salad' },
                        { icon: Activity, color: 'orange', label: 'Movement', title: '15 min walk' }
                    ].map((item, i) => (
                        <label key={i} className="flex items-center gap-3 p-3 bg-white dark:bg-stone-800 rounded-2xl border border-stone-100 dark:border-stone-700 shadow-sm hover:shadow-md hover:border-health-200 dark:hover:border-health-700 transition-all cursor-pointer group/item relative overflow-hidden">
                             <div className="relative">
                                <input type="checkbox" className="peer w-5 h-5 border-2 border-stone-200 dark:border-stone-600 rounded-md checked:bg-health-600 checked:border-health-600 transition-colors appearance-none cursor-pointer" />
                                <svg className="absolute top-0.5 left-0.5 w-4 h-4 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                            </div>
                            <div className="flex-1">
                                <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">{item.label}</p>
                                <p className="font-bold text-gray-800 dark:text-gray-100 text-sm group-hover/item:text-health-700 dark:group-hover:text-health-400 transition-colors">{item.title}</p>
                            </div>
                            <div className={`p-2 bg-${item.color}-50 dark:bg-${item.color}-900/30 text-${item.color}-600 dark:text-${item.color}-400 rounded-lg`}>
                                <item.icon size={16} />
                            </div>
                        </label>
                    ))}
                </div>
            </div>
        </div>

        {/* === CENTER COLUMN: Knowledge, Feed (6 cols) === */}
        <div className="md:col-span-6 space-y-6">

            {/* Feed Highlights (New for improved UX) */}
            <div className="glass-panel rounded-3xl p-6 bg-white/80 dark:bg-stone-900/60 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-white flex items-center gap-2">
                        <Users size={18} className="text-trust-600 dark:text-trust-400" /> Community Highlights
                    </h3>
                    <button className="text-xs font-bold text-trust-600 dark:text-trust-400 hover:underline">View All</button>
                </div>

                <div className="space-y-4">
                    <div className="p-4 bg-stone-50 dark:bg-stone-800 rounded-2xl border border-stone-100 dark:border-stone-700 flex gap-4">
                        <div className="w-10 h-10 rounded-full bg-trust-100 dark:bg-trust-900/30 text-trust-600 dark:text-trust-400 flex items-center justify-center font-bold text-sm shrink-0">SM</div>
                        <div>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">Sarah M. <span className="font-normal text-gray-500">posted in</span> Type 2 Diabetes</p>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">"The cold rice trick really works! My post-meal glucose was significantly lower today..."</p>
                            <div className="flex gap-4 mt-2 text-xs text-gray-500">
                                <span className="flex items-center gap-1"><MessageSquare size={12}/> 8 comments</span>
                                <span>2h ago</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Micro-Education / Info Core */}
            <div className="glass-panel rounded-3xl p-6 bg-gradient-to-br from-trust-50/50 to-white dark:from-trust-900/10 dark:to-stone-800/50 shadow-md">
                 <div className="flex justify-between items-start mb-3">
                    <h3 className="text-md font-bold text-trust-700 dark:text-trust-400 flex items-center gap-2">
                        <BookOpen size={18} />
                        Did you know?
                    </h3>
                 </div>
                 <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-light">
                    <span className="font-bold text-gray-900 dark:text-white">Cooling rice increases resistant starch.</span> This lowers the glycemic index by up to 40%, preventing blood sugar spikes compared to fresh hot rice.
                 </p>
                 <button className="mt-5 text-xs font-bold text-trust-600 dark:text-trust-400 flex items-center hover:underline group uppercase tracking-wider">
                    Read the study <ArrowUpRight size={12} className="ml-1 group-hover:translate-x-0.5 transition-transform" />
                 </button>
            </div>

            {/* Knowledge Base Snippet */}
            <div className="glass-panel rounded-3xl p-6 shadow-sm bg-white/80 dark:bg-stone-900/60">
                 <h3 className="text-lg font-light text-gray-800 dark:text-white mb-4">Latest for You</h3>
                 <div className="grid grid-cols-2 gap-4">
                     {[
                        { title: "Weight Loss Plateaus Explained", time: "3 min read", tag: "Weight Loss" },
                        { title: "Sleep & Metabolism Connection", time: "7 min read", tag: "Science" }
                    ].map((article, i) => (
                        <div key={i} className="p-4 rounded-2xl border border-stone-100 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-800 cursor-pointer transition-colors bg-white dark:bg-stone-800/50">
                             <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-stone-100 dark:bg-stone-700 px-2 py-0.5 rounded-full mb-2 inline-block">{article.tag}</span>
                             <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">{article.title}</h4>
                             <p className="text-xs text-gray-400">{article.time}</p>
                        </div>
                    ))}
                 </div>
            </div>

        </div>

        {/* === RIGHT COLUMN: Gamification & Appointments (3 cols) === */}
        <div className="md:col-span-3 space-y-6">

             {/* Gamification Summary */}
            <div className="glass-panel rounded-3xl p-6 relative overflow-hidden shadow-md bg-white/80 dark:bg-stone-900/60">
                 <div className="flex justify-between items-start mb-4">
                     <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">Daily Progress</p>
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">125 XP</h2>
                     </div>
                 </div>

                 <div className="space-y-2">
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 font-medium">
                        <span>Level {user.currentLevel}</span>
                        <span>Level {user.currentLevel + 1}</span>
                    </div>
                    <div className="w-full bg-stone-100 dark:bg-stone-700 h-2.5 rounded-full overflow-hidden">
                        <div className="bg-gradient-to-r from-orange-400 to-yellow-400 h-full w-[75%] rounded-full shadow-[0_0_10px_rgba(251,191,36,0.5)]"></div>
                    </div>
                 </div>

                 <div className="mt-6 flex flex-col gap-3">
                    <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl border border-stone-100 dark:border-stone-700">
                         <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs font-bold">W</div>
                         <div>
                            <p className="text-xs font-bold text-gray-900 dark:text-white">Walk 10k steps</p>
                            <p className="text-[10px] text-gray-500">Weekly Challenge</p>
                         </div>
                         <CheckCircle size={16} className="ml-auto text-green-500" />
                    </div>
                 </div>
            </div>

            {/* Upcoming Appointment */}
            <div className="glass-panel rounded-3xl p-6 relative shadow-md bg-stone-900 dark:bg-stone-800 text-white">
                <h3 className="text-sm font-bold mb-4 flex items-center gap-2">
                    <Calendar size={16} /> Upcoming
                </h3>
                <div className="bg-white/10 p-4 rounded-xl backdrop-blur-sm border border-white/10 mb-4">
                     <p className="text-xs text-gray-300 mb-1">Tomorrow, 2:00 PM</p>
                     <p className="font-bold">Dr. James Smith</p>
                     <p className="text-xs text-gray-300">Endocrinologist</p>
                </div>
                <button className="w-full py-2 bg-white text-gray-900 rounded-lg text-xs font-bold hover:bg-gray-100 transition-colors">
                    View Details
                </button>
            </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;